import React, { useRef, useContext } from 'react';
import {
  Dialog as MuiDialog,
  Grid,
  Typography,
  makeStyles,
  Button,
} from '@material-ui/core';
import moment from 'moment-timezone';
import { useTheme } from '@material-ui/core/styles';
import { SectionHeader } from '@mummssoftware/common-ui';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CertContext } from '../../Context';
import { withSelectors } from '../CertForm/Components/Selectors';
import messages from '../CertForm/CertMessages';
import { VERBAL_BTN_TEXT_ID } from '../../constants';

const Dialog = styled(MuiDialog)`
  && .MuiPaper-root.MuiDialog-paper {
    align-items: center;
    min-height: 150px;
    min-width: 385px;
    overflow: hidden;
  }
`;

const GridContainer = styled(Grid)`
  max-width: 385px;
  padding: 8px 8px 12px;
`;
const GridItem = styled(Grid)`
  position: relative;
  min-height: 30px;
`;

const StatementBox = styled.div`
  max-width: 385px;
  height: 40px;
  margin-right: 15px;
  margin-left: 15px;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-decoration: underline;
  font-weight: bold;
  text-align: center;
`;

const ButtonBox = styled.div`
  float: right;
`;

const PecosMessageText = styled.p`
  color: ${({ theme }) => theme.palette.error.main};
`;

const BaseMissingParams = withSelectors(
  ({
    PhysPicker,
    DatePicker,
    open,
    handleClose,
    missingPhys,
    missingDate,
    dimensions,
    embedded,
    handleExitMissingParamDialog,
    showPecosMessage,
    isDoctor,
    selectedDoctor,
  }) => {
    const { buttonClicked, lateCert, verbalDate, selectedDate } = useContext(
      CertContext,
    );

    const selectedDateStringLocalTime = moment(selectedDate).format(
      'MM/DD/YYYY',
    );
    const verbalDateLocalTime = moment(verbalDate).format('MM/DD/YYYY');

    const theme = useTheme();
    // NOTE: Overriding styles with classes to customize CSS injected by Material-UI
    const useStyles = makeStyles({
      dialog: {
        position: 'absolute',
        left: dimensions ? dimensions.x : 100,
        top: dimensions ? dimensions.y : 100,
      },
    });
    const classes = useStyles();
    return (
      <Dialog
        classes={
          embedded && dimensions
            ? {
                paper: classes.dialog,
              }
            : {}
        }
        onClose={handleClose}
        aria-labelledby="missing-params-dialog"
        open={open}
      >
        <SectionHeader
          id="missing-params-dialog"
          title={<FormattedMessage {...messages.missingParams} />}
        />
        <GridContainer container spacing={1}>
          {showPecosMessage ? (
            <GridItem xs={12} item>
              <PecosMessageText>
                <strong>
                  {buttonClicked === VERBAL_BTN_TEXT_ID ? (
                    <FormattedMessage
                      {...messages.pecosNurseVerbalMsg}
                      values={{ verbalDate: selectedDateStringLocalTime }}
                    />
                  ) : (
                    <FormattedMessage
                      {...messages.pecosNurseHardCopySigMsg}
                      values={{ verbalDate: verbalDateLocalTime }}
                    />
                  )}
                </strong>
              </PecosMessageText>
            </GridItem>
          ) : null}
          {(() => {
            const dateSection = missingDate && (
              <GridItem xs={12} item key="date">
                <Typography>
                  <FormattedMessage {...messages.certDate} />:
                </Typography>
                {DatePicker}
              </GridItem>
            );
            const physSection = missingPhys && (
              <GridItem xs={12} item key="phys">
                <Typography>
                  <FormattedMessage {...messages.certPhys} />:
                </Typography>
                {PhysPicker}
              </GridItem>
            );
            const dateFirst = isDoctor === false;
            return (
              <>
                {dateFirst ? dateSection : physSection}
                {dateFirst ? physSection : dateSection}
              </>
            );
          })()}

          <GridItem xs={12} item>
            <StatementBox>
              {selectedDate &&
                (lateCert ? (
                  <FormattedMessage
                    {...messages.lateCert}
                    values={{ type: buttonClicked }}
                    style={{
                      color: theme.palette.error.main,
                      borderColor: theme.palette.error.main,
                    }}
                  />
                ) : null)}
            </StatementBox>
          </GridItem>
          <GridItem xs={12} item>
            <ButtonBox>
              {selectedDate &&
                (isDoctor || selectedDoctor) &&
                (lateCert ? (
                  <Button
                    id="lateCert"
                    variant="outlined"
                    style={{
                      color: theme.palette.error.main,
                      borderColor: theme.palette.error.main,
                    }}
                    onClick={handleClose}
                  >
                    <FormattedMessage {...messages.confirmLate} />
                  </Button>
                ) : (
                  !showPecosMessage && (
                    <Button
                      id="lateCert"
                      variant="outlined"
                      color="primary"
                      onClick={handleClose}
                    >
                      <FormattedMessage {...messages.confirmTimely} />
                    </Button>
                  )
                ))}
              <Button
                id="lateCert"
                variant="outlined"
                color="primary"
                onClick={handleExitMissingParamDialog}
              >
                <FormattedMessage {...messages.cancel} />
              </Button>
            </ButtonBox>
          </GridItem>
        </GridContainer>
      </Dialog>
    );
  },
);

export const MissingParams = ({
  fetchCertDoctorsOnDateChange = true,
  hasHospicePhysician = false,
  ...props
}) => {
  const physContainerRef = useRef(null);
  return (
    <BaseMissingParams
      {...props}
      physSelectorId="picker-modal"
      physContainerRef={physContainerRef}
      fetchCertDoctorsOnDateChange={fetchCertDoctorsOnDateChange}
      hasHospicePhysician={hasHospicePhysician}
      blankUntilDateSelected
    />
  );
};
